if (!customElements.get('recently-viewed-products')) {
  customElements.define(
    'recently-viewed-products',
    class RecentlyViewedProducts extends HTMLElement {
      constructor() {
        super();
        this.storageKey = 'recently_viewed_products';
        this.maxStored = 20;
        this.displayLimit = 8;
        this.currentProductHandle = this.dataset.currentProductHandle || '';
        this.sectionId = this.dataset.sectionId || '';
        this.title = this.dataset.title || '';
        this.limit = parseInt(this.dataset.limit) || 8;
        this.swiperInstance = null;
        this.productCount = 0;
        this.cardSequence = 0;
        this.resizeTimeout = null;
        this.resizeHandler = this.handleResize.bind(this);
        this.observer = null;
        this.loadVersion = 0;
      }

      connectedCallback() {
        if (this.getRecentlyViewed().length === 0) {
          this.showEmptyState();
          this.hideSection();
          return;
        }

        this.showSection();
        this.observer = new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            this.loadRecentlyViewed();
          },
          { rootMargin: '0px 0px 400px 0px' },
        );
        this.observer.observe(this);
        window.addEventListener('resize', this.resizeHandler);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.loadVersion += 1;
        // Remove resize listener
        window.removeEventListener('resize', this.resizeHandler);

        // Clear resize timeout if exists
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }

        // Destroy Swiper instance
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }
      }

      // Helper function to get current breakpoint info
      getBreakpointInfo() {
        const width = window.innerWidth;

        if (width >= 990) {
          return { name: 'large', visibleSlides: 5 };
        } else if (width >= 750) {
          return { name: 'desktop', visibleSlides: 4 };
        } else if (width >= 480) {
          return { name: 'tablet', visibleSlides: 2 };
        } else {
          return { name: 'mobile', visibleSlides: 1 };
        }
      }

      // Decision functions
      shouldEnableSlider(productCount, visibleSlides) {
        return productCount > visibleSlides;
      }

      shouldEnableLoop(productCount, visibleSlides) {
        return productCount > visibleSlides;
      }

      shouldShowArrows(productCount, visibleSlides) {
        return productCount > visibleSlides;
      }

      // Handle window resize with debouncing
      handleResize() {
        if (this.resizeTimeout) {
          clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(() => {
          if (this.swiperInstance && this.productCount > 0) {
            const sliderElement = this.querySelector('.recently-viewed-slider');
            if (sliderElement) {
              this.updateSliderConfig(sliderElement, this.productCount);
            }
          }
        }, 250);
      }

      trackProductView(handle) {
        if (!handle || typeof Storage === 'undefined') return;

        try {
          let recentlyViewed = JSON.parse(localStorage.getItem(this.storageKey) || '[]');

          // Remove if already exists (to move to front)
          recentlyViewed = recentlyViewed.filter((h) => h !== handle);

          // Add to front
          recentlyViewed.unshift(handle);

          // Limit to max stored
          if (recentlyViewed.length > this.maxStored) {
            recentlyViewed = recentlyViewed.slice(0, this.maxStored);
          }

          localStorage.setItem(this.storageKey, JSON.stringify(recentlyViewed));
        } catch (error) {
          console.error('Error tracking product view:', error);
        }
      }

      getRecentlyViewed() {
        if (typeof Storage === 'undefined') return [];

        try {
          const stored = localStorage.getItem(this.storageKey);
          if (!stored) return [];

          let handles = JSON.parse(stored);

          // Remove current product if present
          if (this.currentProductHandle) {
            handles = handles.filter((h) => h !== this.currentProductHandle);
          }

          // Limit to display limit
          return handles.slice(0, this.limit);
        } catch (error) {
          console.error('Error getting recently viewed:', error);
          return [];
        }
      }

      async fetchProductData(handle) {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return await response.json();
        } catch (error) {
          console.error(`Error fetching product ${handle}:`, error);
          return null;
        }
      }

      async loadRecentlyViewed() {
        const loadVersion = ++this.loadVersion;
        const handles = this.getRecentlyViewed();

        if (handles.length === 0) {
          this.showEmptyState();
          this.hideSection();
          return;
        }

        // Show section when loading products
        this.showSection();
        this.showLoading();

        try {
          // Fetch all products in parallel with Swiper, so the slider is shown already initialized
          const productPromises = handles.map((handle) => this.fetchProductData(handle));
          const [products] = await Promise.all([Promise.all(productPromises), window.pfLoadSwiper().catch(() => null)]);
          if (!this.isConnected || loadVersion !== this.loadVersion) return;

          // Filter out null results and invalid products
          const validProducts = products.filter((product) => {
            if (!product) return false;

            // Exclude products with hide_storefront tag
            // Tags can be array or comma-separated string
            const tags = Array.isArray(product.tags) ? product.tags : product.tags ? product.tags.split(',').map((t) => t.trim()) : [];

            if (tags.includes('hide_storefront')) {
              return false;
            }

            // Exclude dream team products (check metafields if available)
            // Note: Ajax API may not return all metafields, so we'll check if available
            if (
              product.metafields &&
              product.metafields.custom &&
              product.metafields.custom.dream_team &&
              product.metafields.custom.dream_team.value === true
            ) {
              return false;
            }

            return true;
          });

          if (validProducts.length === 0) {
            this.showEmptyState();
            this.hideSection();
            return;
          }

          // Show section when products are available
          this.showSection();
          this.productCount = validProducts.length;
          this.renderProducts(validProducts);
        } catch (error) {
          if (!this.isConnected || loadVersion !== this.loadVersion) return;
          console.error('Error loading recently viewed products:', error);
          this.showEmptyState();
          this.hideSection();
        }
      }

      renderProducts(products) {
        const container = this.querySelector('.recently-viewed-container');
        const grid = this.querySelector('.recently-viewed-grid');
        const slider = this.querySelector('.recently-viewed-slider');
        const sliderWrapper = slider?.querySelector('.swiper-wrapper');

        if (!container || !grid) return;

        // Show section when products are available
        this.showSection();

        // Hide loading and empty states
        this.hideLoading();
        this.hideEmptyState();

        // Always show slider (for both < 5 and >= 5 products)
        if (slider && sliderWrapper) {
          grid.style.display = 'none';
          slider.style.display = 'block';

          // Create slides
          const slidesHTML = products.map((product) => `<div class="swiper-slide">${this.generateProductCardHTML(product)}</div>`).join('');

          sliderWrapper.innerHTML = slidesHTML;

          // Store product count for resize handling
          this.productCount = products.length;

          // Initialize Swiper
          this.initSlider(slider, products.length);
        } else {
          // Fallback to grid if slider elements not found
          slider.style.display = 'none';
          grid.style.display = 'flex';
          const cardsHTML = products.map((product) => this.generateProductCardHTML(product)).join('');
          grid.innerHTML = cardsHTML;
        }
      }

      generateProductCardHTML(product) {
        // Extract product data - Shopify Ajax API returns different structure
        // Product ID might be numeric or GID format (gid://shopify/Product/123456)
        let productId = product.id || '';
        // Extract numeric ID if it's in GID format
        if (productId && typeof productId === 'string' && productId.includes('gid://')) {
          const parts = productId.split('/');
          productId = parts[parts.length - 1];
        }
        const productHandle = product.handle || '';

        // Handle image - Shopify Ajax API returns featured_image as string URL
        // or images array with string URLs or objects
        let featuredImage = null;
        let imageAlt = product.title || '';

        if (product.featured_image) {
          if (typeof product.featured_image === 'string') {
            featuredImage = product.featured_image;
          } else if (product.featured_image.src) {
            featuredImage = product.featured_image.src;
            imageAlt = product.featured_image.alt || imageAlt;
          } else if (product.featured_image.url) {
            featuredImage = product.featured_image.url;
            imageAlt = product.featured_image.alt || imageAlt;
          }
        } else if (product.images && product.images.length > 0) {
          const firstImage = product.images[0];
          if (typeof firstImage === 'string') {
            featuredImage = firstImage;
          } else if (firstImage.src) {
            featuredImage = firstImage.src;
            imageAlt = firstImage.alt || imageAlt;
          } else if (firstImage.url) {
            featuredImage = firstImage.url;
            imageAlt = firstImage.alt || imageAlt;
          }
        }

        const imageUrl = featuredImage || 'https://cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-product-1_large.png';
        const productUrl = product.url || `/products/${productHandle}`;
        const productTitle = this.formatProductTitle(product.title || '');
        const quickAddId = `recently-${this.sectionId}-${productId}-${++this.cardSequence}`;
        const chooseOptionsLabel = this.escapeHtml(this.dataset.chooseOptions.replace('PRODUCT_NAME_PLACEHOLDER', () => product.title || productTitle));
        
        // Get price from product or first variant
        const productPrice = product.price || (product.variants && product.variants.length > 0 ? product.variants[0].price : 0);
        const productCompareAtPrice =
          product.compare_at_price || (product.variants && product.variants.length > 0 ? product.variants[0].compare_at_price : null);

        const price = this.formatPrice(productPrice);
        const compareAtPrice = productCompareAtPrice ? this.formatPrice(productCompareAtPrice) : null;
        const isOnSale = productCompareAtPrice && productCompareAtPrice > productPrice;
        const isAvailable = product.available !== false;
        const badges = this.getProductBadges(product);

        return `
          <div class="recently-viewed-card-wrapper">
            <div class="card-product2">
              <div class="card-product2__image-container">
                <a href="${productUrl}" class="full-unstyled-link"${!featuredImage ? ` aria-label="${this.escapeHtml(product.title || productTitle)}"` : ''}>
                  ${featuredImage ? this.generateImageHTML(imageUrl, imageAlt) : ''}
                  ${badges ? `<span class="card__badge">${badges}</span>` : ''}
                </a>
                <div class="quick-add quick-add-card no-js-hidden medium-hide large-up-hide">
                  <modal-opener data-modal="#QuickAdd-${quickAddId}">
                    <button
                      id="quick-add-${quickAddId}-submit"
                      type="submit"
                      name="add"
                      class="quick-add__submit"
                      aria-haspopup="dialog"
                      aria-label="${chooseOptionsLabel}"
                      data-product-url="${productUrl}"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 53.41 55.72" class="icon-quick-add">
                        <path fill="none" stroke="#000" stroke-miterlimit="10" stroke-width="2.5" d="M14.33,20.24v-11.61c0-1.6.59-3.14,1.68-4.3,1.49-1.59,3.65-3.05,6.51-3.08,2.26-.02,4.94.21,7.54,3.23.97,1.12,1.49,2.59,1.49,4.09v11.67"></path>
                        <path fill="none" stroke="#000" stroke-miterlimit="10" stroke-width="2.5" d="M28.55,40.93c.68-5.04,4.65-9.18,9.67-10.03,1.55-.26,3.06-.23,4.47.06-.14-4.14-.21-6.69-.41-9.1-.56-7.06-8-7.32-8-7.32H10.76c-7.69-.17-7.95,7.39-7.95,7.39,0,0-1.27,16.24-1.55,24.71.13,4.89,3.86,6.78,6.02,7.47.79.25,1.61.36,2.44.36,4.53,0,24.36-.03,30.59-.03.64.08-1.73-.02-1.22-.06-6.5-.66-11.45-6.59-10.53-13.45Z"></path>
                        <path fill="none" stroke="#000" stroke-miterlimit="10" stroke-width="2.5" d="M39.08,54.37c.4.04.81.06,1.22.06,6.55,0,11.86-5.31,11.86-11.86,0-5.73-4.06-10.51-9.46-11.62"></path>
                        <line fill="none" stroke="#000" stroke-miterlimit="10" stroke-width="2.5" x1="40.5" y1="36.47" x2="40.5" y2="48.52"></line>
                        <line fill="none" stroke="#000" stroke-miterlimit="10" stroke-width="2.5" x1="46.52" y1="42.5" x2="34.47" y2="42.5"></line>
                      </svg>
                      <span class="loading__spinner hidden">
                        <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
                        </svg>
                      </span>
                    </button>
                  </modal-opener>
                  <quick-add-modal id="QuickAdd-${quickAddId}" class="quick-add-modal">
                    <div
                      role="dialog"
                      aria-label="Choose product options"
                      aria-modal="true"
                      class="quick-add-modal__content global-settings-popup"
                      tabindex="-1"
                    >
                      <button
                        id="ModalClose-${quickAddId}"
                        type="button"
                        class="quick-add-modal__toggle"
                        aria-label="Close"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 24 24">
                          <path fill="currentColor" fill-rule="evenodd" d="M5 17.586a1 1 0 1 0 1.415 1.415L12 13.414 17.586 19A1 1 0 0 0 19 17.586L13.414 12 19 6.414A1 1 0 0 0 17.585 5L12 10.586 6.414 5A1 1 0 0 0 5 6.414L10.586 12z" clip-rule="evenodd" opacity="1"></path>
                        </svg>
                      </button>
                      <h3 class="quick-add-modal__heading">QUICK SHOP</h3>
                      <div id="QuickAddInfo-${quickAddId}" class="quick-add-modal__content-info"></div>
                    </div>
                  </quick-add-modal>
                </div>
              </div>
                <div class="card-product2__info">
                <h6 class="card-product2__title" aria-level="3">
                  <a href="${productUrl}" class="full-unstyled-link">${this.escapeHtml(productTitle)}</a>
                </h6>
                <div class="card-product2__price">
                  ${isOnSale ? `<span class="price-sale">${price}</span><span class="price-compare">${compareAtPrice}</span>` : `<span class="price-regular">${price}</span>`}
                </div>
              </div>
            </div>
          </div>
        `;
      }

      generateImageHTML(imageUrl, alt) {
        // Generate responsive image srcset
        // Shopify CDN supports width parameter
        const widths = [165, 360, 533, 720, 940, 1066];
        const srcset = widths
          .map((width) => {
            // Handle both full URLs and relative paths
            const separator = imageUrl.includes('?') ? '&' : '?';
            return `${imageUrl}${separator}width=${width} ${width}w`;
          })
          .join(', ');

        // Get base URL for src
        const separator = imageUrl.includes('?') ? '&' : '?';
        const src = `${imageUrl}${separator}width=533`;

        return `
          <div class="media media--portrait">
            <img
              srcset="${srcset}"
              src="${src}"
              sizes="(min-width: 990px) calc((100vw - 130px) / 4), (min-width: 750px) calc((100vw - 120px) / 3), calc((100vw - 35px) / 2)"
              alt="${this.escapeHtml(alt)}"
              class="motion-reduce"
              loading="lazy"
              width="533"
              height="533"
            >
          </div>
        `;
      }

      escapeHtml(text) {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML.replace(/"/g, '&quot;');
      }

      formatProductTitle(title) {
        if (!title) return '';

        // Check if there's a secondary title metafield (we can't access it from JSON, so use title)
        const titleParts = title.split('-');
        const partsLength = titleParts.length;

        if (partsLength === 2) {
          return `${titleParts[1].trim()} in ${titleParts[0].trim()}`;
        } else if (partsLength === 3) {
          return `${titleParts[1].trim()} in ${titleParts[0].trim()}`;
        } else if (partsLength === 4) {
          return `${titleParts[1].trim()}-${titleParts[2].trim()} in ${titleParts[0].trim()}`;
        } else if (partsLength === 5) {
          return `${titleParts[1].trim()}- ${titleParts[2].trim()}-${titleParts[3].trim()} in ${titleParts[0].trim()}`;
        }

        return title;
      }

      formatPrice(price) {
        if (!price) return '$0';

        let formatted;
        if (typeof formatMoney !== 'undefined' && window.moneyFormat) {
          formatted = formatMoney(price, window.moneyFormat).replace(/\.00$/, '');
        } else {
          const priceInDollars = price / 100;
          formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(priceInDollars);
          formatted = formatted.replace(/\.00$/, '');
        }

        if (window.currencyCodeEnabled && window.cartCurrency) {
          formatted = formatted + ' ' + window.cartCurrency.code;
        }

        return formatted;
      }

      getProductBadges(product) {
        if (!product.available && product.available !== undefined) {
          return '<div class="cus-col-badge"><span class="span cus-s-o">Sold Out</span></div>';
        }

        // Check for tags that match badge menu (we can't access settings from JS, so skip for now)
        // This would require additional data from the server

        return '';
      }

      initSlider(sliderElement, productCount) {
        if (!window.Swiper) {
          console.error('Swiper is not loaded');
          return;
        }

        // Destroy existing instance if any
        if (this.swiperInstance) {
          this.swiperInstance.destroy(true, true);
          this.swiperInstance = null;
        }

        // Get current breakpoint info
        const breakpointInfo = this.getBreakpointInfo();
        const { visibleSlides } = breakpointInfo;

        // Make decisions based on product count and visible slides
        const enableSlider = this.shouldEnableSlider(productCount, visibleSlides);
        const enableLoop = this.shouldEnableLoop(productCount, visibleSlides);
        const showArrows = this.shouldShowArrows(productCount, visibleSlides);

        // Get arrow elements
        const nextButton = sliderElement.querySelector('.swiper-button-next');
        const prevButton = sliderElement.querySelector('.swiper-button-prev');

        // Show/hide arrows based on decision
        if (showArrows) {
          sliderElement.classList.remove('recently-viewed-slider--no-arrows');
          if (nextButton) nextButton.style.display = 'flex';
          if (prevButton) prevButton.style.display = 'flex';
        } else {
          sliderElement.classList.add('recently-viewed-slider--no-arrows');
          if (nextButton) nextButton.style.display = 'none';
          if (prevButton) prevButton.style.display = 'none';
        }

        // Initialize Swiper with appropriate config
        this.swiperInstance = new Swiper(sliderElement, {
          slidesPerView: 'auto',
          spaceBetween: 0,
          loop: enableLoop,
          watchOverflow: true,
          allowSlideNext: enableSlider,
          allowSlidePrev: enableSlider,
          navigation: showArrows
            ? {
                nextEl: nextButton,
                prevEl: prevButton,
              }
            : false,
        });

        // If slider shouldn't be enabled, disable it
        if (!enableSlider && this.swiperInstance) {
          this.swiperInstance.allowTouchMove = false;
          this.swiperInstance.allowSlideNext = false;
          this.swiperInstance.allowSlidePrev = false;
        }
      }

      // Update slider configuration on resize
      updateSliderConfig(sliderElement, productCount) {
        if (!this.swiperInstance) return;

        // Get current breakpoint info
        const breakpointInfo = this.getBreakpointInfo();
        const { visibleSlides } = breakpointInfo;

        // Make decisions based on product count and visible slides
        const enableSlider = this.shouldEnableSlider(productCount, visibleSlides);
        const enableLoop = this.shouldEnableLoop(productCount, visibleSlides);
        const showArrows = this.shouldShowArrows(productCount, visibleSlides);

        // Get arrow elements
        const nextButton = sliderElement.querySelector('.swiper-button-next');
        const prevButton = sliderElement.querySelector('.swiper-button-prev');

        // Update loop
        if (enableLoop !== this.swiperInstance.params.loop) {
          // Swiper doesn't support changing loop dynamically, need to reinitialize
          this.initSlider(sliderElement, productCount);
          return;
        }

        // Update arrow visibility
        if (showArrows) {
          sliderElement.classList.remove('recently-viewed-slider--no-arrows');
          if (nextButton) nextButton.style.display = 'flex';
          if (prevButton) prevButton.style.display = 'flex';
        } else {
          sliderElement.classList.add('recently-viewed-slider--no-arrows');
          if (nextButton) nextButton.style.display = 'none';
          if (prevButton) prevButton.style.display = 'none';
        }

        // Update slider enable/disable state
        this.swiperInstance.allowTouchMove = enableSlider;
        this.swiperInstance.allowSlideNext = enableSlider;
        this.swiperInstance.allowSlidePrev = enableSlider;

        // Update navigation
        if (showArrows && nextButton && prevButton) {
          this.swiperInstance.params.navigation = {
            nextEl: nextButton,
            prevEl: prevButton,
          };
          this.swiperInstance.navigation.init();
          this.swiperInstance.navigation.update();
        } else {
          this.swiperInstance.params.navigation = false;
          if (this.swiperInstance.navigation) {
            this.swiperInstance.navigation.destroy();
          }
        }

        // Update Swiper
        this.swiperInstance.update();
      }

      showLoading() {
        const loading = this.querySelector('.recently-viewed-loading');
        if (loading) loading.style.display = 'block';
      }

      hideLoading() {
        const loading = this.querySelector('.recently-viewed-loading');
        if (loading) loading.style.display = 'none';
      }

      showEmptyState() {
        const empty = this.querySelector('.recently-viewed-empty');
        if (empty) empty.style.display = 'block';
        this.hideLoading();
      }

      hideEmptyState() {
        const empty = this.querySelector('.recently-viewed-empty');
        if (empty) empty.style.display = 'none';
      }

      hideSection() {
        // Add hidden class to the main element to hide the entire section
        this.classList.add('hidden');
      }

      showSection() {
        // Remove hidden class to show the section
        this.classList.remove('hidden');
      }
    },
  );
}
